/**
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import { isNonEmptyArray } from '../../util.type';
import PromiseUtil from '../promise/promise-util';
import { assertIsServerDeviceId } from '../session/session-attrs';
import { isAnalyticsCounterAttrs } from './analytics-counter-attrs';
import {
  isAnalyticsGroupVxAttrs,
  isAnalyticsGroupVxV2Attrs
} from './analytics-group-attrs';
import { errorWithAnalyticsStackTrace } from './analytics-transporter';

/**
 * @import { IAnalyticsTransporter } from '.'
 * @import { MaybeServerDeviceId } from '../session/session-attrs'
 * @import Store from '../store/store'
 * @import { AnalyticsCounter } from './analytics-counter'
 * @import { AnalyticsGroup, MaybeAnalyticsGroupVx } from './analytics-group'
 * @import { AnalyticsGroupAttrs } from './analytics-group-attrs'
 */

/**
 * Migrátor pro analytická data z verze 1 na verzi 2, best-effort odesílač
 * pro verzi 2 na jiné než aktuální ServerDeviceId a mazač prázdných v2 skupin.
 */
export default class AnalyticsMigrator {
  /**
   * @param {IAnalyticsTransporter} analyticsTransporter
   * @param {MaybeServerDeviceId} session
   * @param {Store} store
   */
  constructor(analyticsTransporter, session, store) {
    this.analyticsTransporter = analyticsTransporter;
    this.session = session;
    this.store = store;
  }

  /**
   * Migrácia analytických dát (popis algoritmu, včetně preconditions
   * a postconditions v README.md.
   *
   * Odesílání se děje v opačném kauzálním pořadí, protože chceme typicky hlavně
   * odeslat poslední uzavřenou skupinu. Pokud by totiž byly problémy např.
   * s firewallem a spousta neodeslaných dat, tak pak by mohl každý request
   * timeoutovat a celá migrace blokovat spuštění aplikace.
   *
   * TODO #389: Chtělo by to na migraci mít nějaký globální timeout pro odesílání
   * a po jeho vypršení již se ani nepokoušet o odeslání dat, jen mazat.
   * @param {Error[] | undefined} warnings Volitelné pole pro pushování
   * transportních chyb, což při best-effort odesílání jsou vlastně warningy.
   * @throws {Error} Pokud nastala chyba, kterou by se porušily postcondition
   * migrace, tzn. že se v tomto případě nesmí nastartovat sběr analytiky!
   */
  async migrate(warnings) {
    assertIsServerDeviceId(this.session);
    const { serverUrl, deviceId } = this.session;

    /** @type {AnalyticsGroup['id'][]} */
    const keepGroupIds = [];

    // Výsledek se musí přetypovat k horšímu jen zde v migraci, aby dobře
    // fungovaly type guardy níže a ostatní kód mimo migrátor nemusel validovat.
    /** @type {MaybeAnalyticsGroupVx[]} */
    const groups = await this.store.findAllAnalyticsGroup({
      orderBy: [['opened', 'DESC']]
    });

    if (isNonEmptyArray(groups)) {
      await PromiseUtil.allSerial(
        groups.map((group) => async () => {
          if (isAnalyticsGroupVxAttrs(group)) {
            /** @type {AnalyticsCounter['id'][]} */
            const invalidCounterIds = [];
            const counters = await this.store.findAllAnalyticsCounter({
              where: { analyticsGroupId: group.id }
            });
            const validCounters = counters.reduce((acc, counter) => {
              const counterId = counter.id;
              if (isAnalyticsCounterAttrs(counter)) {
                acc.push(counter);
              } else {
                invalidCounterIds.push(counterId);
              }
              return acc;
            }, /** @type {AnalyticsCounter[]} */ ([]));

            // Skupiny s počítadly se zpracují, ty bez počítadel se vždy smažou,
            // včetně případných validních i nevalidních počítadel.
            if (isNonEmptyArray(validCounters)) {
              const isGroupV2 = isAnalyticsGroupVxV2Attrs(group);

              // Jen v2 skupiny se stejným ServerDeviceId se ponechávají.
              if (
                isGroupV2 &&
                group.serverUrl === serverUrl &&
                group.deviceId === deviceId
              ) {
                // Případná nevalidní počítadla se promažou.
                if (isNonEmptyArray(invalidCounterIds)) {
                  await this.store.destroyAllAnalyticsCounter({
                    where: { id: { in: invalidCounterIds } }
                  });
                }

                // Pro mazaní sirotků na konci.
                keepGroupIds.push(group.id);
                return;
              }

              // v1 a v2 skupiny s jiným ServerDeviceId se best-effort odesílají.
              // TODO #389: Pozor v testech, že data odeslaná do transportu se
              // mohou lišit podle v1/v2, protože se pokaždé jinak dělá zpracování.
              // Proto vznikly pomocné funkce v analytics-transporter.spec.unit.js
              // pro deterministické porovnání v testech.
              /** @type {AnalyticsGroupAttrs} */
              let transportGroup;
              if (isGroupV2) {
                // v2 skupiny se předají as-is referencí.
                transportGroup = group;
              } else {
                // v1 skupiny se namigrují kopií (kvůli problémům s JSData, kdy
                // in-place modifikace na singletonu se mohly kdykoliv vedlejším
                // efektem práce s JSData přepsat).
                transportGroup = {
                  ...group,
                  // Případné uzavření intervalu pomocí stejné hodnoty jako v opened.
                  // Tím se sice vyrobí skupina s délkou 0s (což je validní a běžný
                  // stav v budoucnu pro neagregovaný sběr, ale v tomto případě vždy
                  // s hodnotou played 1), ale to se stane jednorázově při migraci
                  // z v1 neuzavřených skupin, takže podle played > 1 to bude
                  // rozlišitelné.
                  closed: group.closed ?? group.opened,
                  // Doplnění ServerDeviceId, což je i zarážka pro nemigraci
                  // v dalším běhu migrátoru.
                  serverUrl,
                  deviceId
                };
              }

              await this.analyticsTransporter
                // Zkrácení timeout z výchozích 30s na 5s, kvůli best-effort
                // a případnému většímu množství neodeslaných dat.
                .transport(transportGroup, validCounters, {
                  timeoutInMs: 5000
                })
                .catch((error) => {
                  const transportError = errorWithAnalyticsStackTrace(
                    /** @type {Error} */ (error),
                    transportGroup,
                    validCounters
                  );
                  // TODO #389: Obohacení o ServerDeviceId by chtělo dělat groupBy
                  // ServerDeviceId a AggregateError nesoucí deduplikované údaje
                  // k právě jedné kombinaci.
                  // TODO #389: Nebo message měnit před voláním, ať je duplikovaná
                  // ve stack trace? Takto je to ale kratší a snad nese potřebné
                  // informace.
                  transportError.message = `transport error (serverUrl=${
                    transportGroup.serverUrl
                  } deviceId=${transportGroup.deviceId})${
                    transportError.message ? `: ${transportError.message}` : ''
                  }`;
                  warnings?.push(transportError);
                });
            }
          }

          await this.store.destroyAnalyticsGroup(group.id);
          await this.store.destroyAllAnalyticsCounter({
            where: { analyticsGroupId: group.id }
          });
        })
      );
    }

    // Mazání případných osirocených počítadel.
    if (isNonEmptyArray(keepGroupIds)) {
      await this.store.destroyAllAnalyticsCounter({
        where: { analyticsGroupId: { notIn: keepGroupIds } }
      });
    } else {
      await this.store.destroyAllAnalyticsCounter();
    }
  }
}
